// Read-only Sales view of income already posted through accounting memos.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { t } from '../shared/i18n.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { fmtMoney, formatDate } from './bank_helpers.js';
import { tenantShell } from './tshell.js';
import { SALES_TABS } from './documents.js';

export function createSalesMemoState(){
  return { response: signal(null), loading: signal(true), error: signal(''), query: signal(''),
    dateFrom: signal(''), dateTo: signal(''), page: signal(1), perPage: signal(DEFAULT_PER_PAGE) };
}

export function memoDetailHref(companyId, memo){
  return '/co/' + companyId + '/sales-memos/' + memo.id;
}

export function salesMemosContent(companyId, state, reload){
  function resetAndReload(){ state.page.value=1;reload(); }
  const list = () => {
    const loading=state.loading.value,error=state.error.value,response=state.response.value;
    if(loading) return html`<div class="muted-block" role="status">${t('common.loading','Loading…')}</div>`;
    if(error) return html`<div class="muted-block" role="alert">${error}
      <button type="button" class="btn btn-sm btn-outline" @click=${reload}>${t('common.retry','Retry')}</button></div>`;
    if(!response) return html`<div class="muted-block" role="alert">${t('sales_memos.unavailable','Sales memos are unavailable.')}</div>`;
    const company=response.company,rows=response.data;
    const currency=company.currency_symbol || company.base_currency_symbol || company.currency_code || company.base_currency_code;
    const dateFormat=company.date_format || company.country_date_format;
    const pagination=() => pager({page:response.page,totalPages:response.total_pages,total:response.total,
      perPage:response.per_page,onPage:page => {state.page.value=page;reload();}});
    const referenceLink=memo => html`<a href=${memoDetailHref(companyId,memo)}
      title=${t('sales_memos.open_details','Open memo details')}>${memo.reference || '#' + memo.id}</a>`;
    return html`<div class="list-wrap">
      <div class="sales-memos-summary">${t('sales_memos.filtered_total','Net income — all matching memos')}
        <strong class="mono">${fmtMoney(response.total_net_income,currency,true)}</strong></div>
      ${pagination()}
      ${rows.length ? html`<div class="tbl-wrap"><table class="list sales-memos-list">
        <thead><tr><th>${t('common.date','Date')}</th><th>${t('common.reference','Reference')}</th>
          <th>${t('common.description','Description')}</th><th class="ta-r">${t('sales_memos.net_income','Net income')}</th></tr></thead>
        <tbody>${rows.map(memo => html`<tr>
          <td class="mono">${formatDate(memo.date,dateFormat)}</td><td class="mono">${referenceLink(memo)}</td>
          <td>${memo.description || ''}</td><td class="mono ta-r">${fmtMoney(memo.net_income,currency,true)}</td></tr>`)}</tbody>
      </table></div>` : html`<div class="empty muted-block">${t('sales_memos.no_data','No posted sales memos match these filters.')}</div>`}
      ${pagination()}
    </div>`;
  };
  return html`<div class="page page-wide ct-page">
    <div class="page-head"><h1>${t('nav.sales_v2','Sales')}</h1></div>
    <nav class="segs sales-memos-tabs" aria-label=${t('sales_memos.sales_views','Sales views')}>
      ${SALES_TABS.map(([key,label]) => html`<a class=${'seg' + (key === 'sales-memos' ? ' on' : '')}
        href=${'/co/' + companyId + '/' + key} aria-current=${key === 'sales-memos' ? 'page' : null}>${key === 'sales-memos' ? t('sales_memos.tab','Memos') : label}</a>`)}
    </nav>
    <p class="muted sales-memos-help">${t('sales_memos.help','Income already posted through accounting memos. Open a reference to view its ledger entries.')}</p>
    <div class="sales-memos-filters">
      <label class="sales-memos-search">${t('common.search','Search')}
        <input class="form-control" type="search" .value=${state.query}
          placeholder=${t('sales_memos.search_example','e.g. SD20261001 or daily sales')}
          @input=${event => {state.query.value=event.target.value;resetAndReload();}}></label>
      <label>${t('reports.from','From')}
        <sleek-date data-free="true" .value=${state.dateFrom} @change=${event => {state.dateFrom.value=event.target.value;resetAndReload();}}></sleek-date></label>
      <label>${t('reports.to','To')}
        <sleek-date data-free="true" .value=${state.dateTo} @change=${event => {state.dateTo.value=event.target.value;resetAndReload();}}></sleek-date></label>
      <label>${t('common.rows','Rows')}${perPageSelect(state.perPage.value,event => {state.perPage.value=event.target.value;resetAndReload();})}</label>
    </div>
    ${list}
  </div>`;
}

// Both read-only endpoints use the same stale-response gate and existing API auth handling.
function memoLoader(companyId,state,memoId){
  let requestId=0;
  async function load(){
    const currentRequest=++requestId;
    state.loading.value=true;state.error.value='';
    const params=new URLSearchParams({page:String(state.page.value),per_page:String(state.perPage.value)});
    if(!memoId){
      if(state.query.value.trim()) params.set('q',state.query.value.trim());
      if(state.dateFrom.value) params.set('date_from',state.dateFrom.value);
      if(state.dateTo.value) params.set('date_to',state.dateTo.value);
    }
    try {
      const response=await api.get('/companies/' + companyId + '/sales-memos' + (memoId ? '/' + memoId : '') + '?' + params);
      if(currentRequest !== requestId) return;
      if(!response || !Array.isArray(response.data) || !response.company
        || (memoId ? !response.journal || response.total_debit == null || response.total_credit == null : response.total_net_income == null))
        throw new Error(t('sales_memos.unavailable','Sales memos are unavailable.'));
      // Defaults come from the company server, not browser guesses about its financial year.
      if(!memoId){state.dateFrom.value=response.date_from;state.dateTo.value=response.date_to;}
      state.page.value=response.page;state.response.value=response;
      configureSleekDate(response.company);
    } catch(error){
      if(currentRequest !== requestId) return;
      state.response.value=null;
      state.error.value=error.status === 403 ? t('sales_memos.forbidden','You do not have permission to view sales memos.')
        : (error.data && error.data.error) || error.message || t('sales_memos.load_failed','Could not load sales memos.');
    } finally {if(currentRequest === requestId) state.loading.value=false;}
  }
  return load;
}

export function salesMemosScreen(companyId){
  const state=createSalesMemoState(),load=memoLoader(companyId,state);
  load();
  return tenantShell(companyId,t('nav.sales_v2','Sales'),salesMemosContent(companyId,state,load));
}

export function salesMemoDetailsContent(companyId,state,reload){
  const details=() => {
    const loading=state.loading.value,error=state.error.value,response=state.response.value;
    if(loading) return html`<div class="muted-block" role="status">${t('common.loading','Loading…')}</div>`;
    if(error || !response) return html`<div class="muted-block" role="alert">${error || t('sales_memos.unavailable','Sales memos are unavailable.')}
      <button type="button" class="btn btn-sm btn-outline" @click=${reload}>${t('common.retry','Retry')}</button></div>`;
    const company=response.company,memo=response.journal;
    const currency=company.currency_symbol || company.currency_code;
    const pagination=() => pager({page:response.page,totalPages:response.total_pages,total:response.total,
      perPage:response.per_page,onPage:page => {state.page.value=page;reload();}});
    return html`<div class="list-wrap">
      <h2>${memo.reference || '#' + memo.id}</h2>
      <p><span class="mono">${formatDate(memo.date,company.date_format || company.country_date_format)}</span> · ${memo.description || ''}</p>
      <div class="sales-memos-summary"><span>${t('sales_memos.net_income','Net income')}</span>
        <strong class="mono">${fmtMoney(memo.net_income,currency,true)}</strong></div>
      <div class="sales-memos-summary"><span>${t('sales_memos.all_lines','All journal lines')} — ${t('common.debit','Debit')}
        <strong class="mono">${fmtMoney(response.total_debit,currency,true)}</strong></span>
        <span>${t('common.credit','Credit')} <strong class="mono">${fmtMoney(response.total_credit,currency,true)}</strong></span></div>
      ${pagination()}
      <div class="tbl-wrap"><table class="list sales-memos-list sales-memos-detail-list">
        <thead><tr><th>${t('common.account','Account')}</th><th>${t('common.description','Description')}</th>
          <th class="ta-r">${t('common.debit','Debit')}</th><th class="ta-r">${t('common.credit','Credit')}</th></tr></thead>
        <tbody>${response.data.map(line => html`<tr>
          <td><span class="mono">${line.code}</span> ${line.name}</td><td>${line.description || ''}</td>
          <td class="mono ta-r">${fmtMoney(line.debit,currency,true)}</td><td class="mono ta-r">${fmtMoney(line.credit,currency,true)}</td></tr>`)}</tbody>
      </table></div>${pagination()}
    </div>`;
  };
  return html`<div class="page page-wide ct-page">
    <div class="page-head"><h1>${t('sales_memos.detail_title','Sales memo details')}</h1></div>
    <a class="btn btn-sm btn-outline" href=${'/co/' + companyId + '/sales-memos'}>← ${t('sales_memos.tab','Memos')}</a>
    ${details}
  </div>`;
}

export function salesMemoDetailScreen(companyId,memoId){
  const state=createSalesMemoState(),load=memoLoader(companyId,state,memoId);
  load();
  return tenantShell(companyId,t('nav.sales_v2','Sales'),salesMemoDetailsContent(companyId,state,load));
}
